import { useDisplaySettings } from '@src/providers/AppConfigProvider';
import { useCounterpartiesQueryData } from '@src/providers/Counterparties/useCounterpartiesQueryData';
import {
  Alert,
  AlertVariants,
  BlotterTable,
  Box,
  type ColumnDef,
  Dialog,
  DialogContent,
  DialogFooter,
  EMPTY_ARRAY,
  formattedDate,
  HStack,
  Input,
  Label,
  Text,
  useBehaviorSubject,
  useBlotterTable,
  type useDisclosureWithContext,
  useMarketAccountsContext,
  VStack,
} from '@talos/kyoko';
import { uniqBy } from 'lodash-es';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useCreateManualBatchMutation, usePreviewSettlementBatchQuery } from '../useLpSettlementApi';
import type { NewManualBatchDisclosureContext } from './UnsettledTransactionsBlotter';

interface NewManualBatchDialogProps {
  onSubmit: (batchID: string) => void;
}

interface NewManualBatchDialogProps extends ReturnType<
  typeof useDisclosureWithContext<NewManualBatchDisclosureContext>
> {}

interface ManualBatchItem {
  RowID: string;
  Direction: 'Outgoing' | 'Incoming';
  Asset: string;
  Quantity: string;
  EquivalentAmount: string | undefined;
  EquivalentCurrency: string;
}

export const NewManualBatchDialog = ({ onSubmit, ...disclosure }: NewManualBatchDialogProps) => {
  return (
    <Dialog
      {...disclosure}
      closeOnEscape
      title="New Manual Batch"
      showClose
      width={520}
      controlledContent
      suppressAutoEnterForm
      data-testid="new-manual-batch-dialog"
    >
      {disclosure.context && (
        <NewManualBatchDialogContent disclosure={disclosure} context={disclosure.context} onSubmit={onSubmit} />
      )}
    </Dialog>
  );
};

interface NewManualBatchDialogContentProps {
  context: NewManualBatchDisclosureContext;
  disclosure: ReturnType<typeof useDisclosureWithContext<NewManualBatchDisclosureContext>>;
  onSubmit: (batchID: string) => void;
}

function NewManualBatchDialogContent({ context, disclosure, onSubmit }: NewManualBatchDialogContentProps) {
  const { marketAccountsByName } = useMarketAccountsContext();
  const { counterpartiesByName } = useCounterpartiesQueryData();
  const { homeCurrency } = useDisplaySettings();

  const selectedTrades = uniqBy(context?.selectedTrades ?? EMPTY_ARRAY, item => item.EventID);

  // Calculate summary data from selected trades
  const summaryData = useMemo(() => {
    if (selectedTrades.length === 0) {
      return {
        counterparty: '',
        marketAccount: '',
        totalTransactions: '',
      };
    }

    return {
      counterparty: selectedTrades[0].Counterparty ?? '',
      marketAccount: selectedTrades[0].MarketAccount,
      totalTransactions: selectedTrades.length.toString(),
    };
  }, [selectedTrades]);

  const columns = useMemo<ColumnDef<ManualBatchItem>[]>(() => {
    return [
      { type: 'text', field: 'Direction', title: 'Direction', width: 120, initialSort: '+' },
      { type: 'currency', field: 'Asset', title: 'Asset', width: 100, params: { showDescription: false } },
      {
        type: 'size',
        field: 'Quantity',
        title: 'Quantity',
        width: 150,
        params: { currencyField: 'Asset', highlightNegative: true },
      },
      {
        type: 'size',
        field: 'EquivalentAmount',
        title: `Equivalent (${homeCurrency})`,
        width: 150,
        params: { currencyField: 'EquivalentCurrency', highlightNegative: true },
      },
    ];
  }, [homeCurrency]);

  const {
    isLoading: isPreviewLoading,
    data: batchData,
    isError: isPreviewError,
    refetch: retryPreviewSettlement,
    isSuccess: isPreviewSuccess,
  } = usePreviewSettlementBatchQuery({ selectedTrades, homeCurrency }, { skip: selectedTrades.length === 0 });
  const [createManualBatch, { isLoading: busy }] = useCreateManualBatchMutation();

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (isPreviewError) {
        retryPreviewSettlement();
      } else {
        if (!selectedTrades || selectedTrades.length === 0) {
          return;
        }
        createManualBatch({ selectedTrades, homeCurrency })
          .unwrap()
          .then(response => {
            const batchID = response?.[0]?.SettleBatch?.BatchID ?? '';
            onSubmit(batchID);
          });
      }
    },
    [selectedTrades, createManualBatch, homeCurrency, onSubmit, isPreviewError, retryPreviewSettlement]
  );

  const settleBatchInstructions = useMemo(() => batchData?.[0]?.SettleBatchInstructions ?? EMPTY_ARRAY, [batchData]);

  const { observable: dataObservable } = useBehaviorSubject(() => {
    const result: ManualBatchItem[] = settleBatchInstructions.map((instruction, index) => ({
      RowID: index.toString(),
      Direction: instruction.Direction === 'Outbound' ? 'Outgoing' : 'Incoming',
      Asset: instruction.Asset,
      Quantity: instruction.Amount ?? '0',
      EquivalentAmount: instruction.EquivalentAmount,
      EquivalentCurrency: instruction.EquivalentCurrency,
    }));
    return {
      data: result,
      initial: true,
    };
  }, [settleBatchInstructions]);

  const blotterTable = useBlotterTable<ManualBatchItem>({
    dataObservable,
    errorObservable: undefined,
    columns,
    rowID: 'RowID' satisfies keyof ManualBatchItem,
    gridOptions: {
      loading: isPreviewLoading,
      domLayout: 'autoHeight',
      onFirstDataRendered: ({ api }) => {
        api.sizeColumnsToFit();
      },
    },
  });

  // Calculate minimum SettleDeadline from selected trades
  const settlementDue = useMemo(() => {
    const deadlines = selectedTrades.map(trade => trade.SettleDeadline) ?? EMPTY_ARRAY;
    return deadlines.length > 0
      ? deadlines.reduce((min, current) => (current < min ? current : min), deadlines[0])
      : '';
  }, [selectedTrades]);

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto' }}>
        <form onSubmit={handleSubmit} id="new-manual-batch-modal-form">
          <VStack w="100%" gap="spacing8">
            <SettlementDetail
              title="Counterparty"
              text={counterpartiesByName?.get(summaryData.counterparty)?.DisplayName ?? summaryData.counterparty}
              data-testid="counterparty"
            />
            <SettlementDetail
              title="Market Account"
              text={marketAccountsByName.get(summaryData.marketAccount)?.DisplayName ?? summaryData.marketAccount}
              data-testid="market-account"
            />
            <SettlementDetail
              title="Total Transactions"
              text={summaryData.totalTransactions}
              data-testid="total-transactions"
            />
          </VStack>

          {!isPreviewError && (
            <div data-testid="manual-batch-items-blotter">
              <BlotterTable {...blotterTable} />
            </div>
          )}
          {isPreviewError && (
            <Box mt="spacing12" w="100%" data-testid="manual-batch-error">
              <Alert variant={AlertVariants.Negative} dismissable={false}>
                Failed to generate settlement preview
              </Alert>
            </Box>
          )}

          {/* Settlement Due Field */}
          <VStack mt="spacing12" gap="spacing8" alignItems="flex-start">
            <Label>Settlement Due</Label>
            <Input
              value={formattedDate(settlementDue)}
              readOnly
              placeholder="Settlement due date"
              data-testid="settlement-due-input"
            />
          </VStack>
        </form>
      </DialogContent>
      <Box style={{ order: 3 }}>
        <DialogFooter
          showCancel
          showConfirm
          cancelLabel="Cancel"
          confirmLabel={isPreviewError ? 'Retry' : 'Create Manual Batch'}
          confirmDisabled={isPreviewLoading || busy || (isPreviewSuccess && settleBatchInstructions.length === 0)}
          confirmButtonType="submit"
          confirmButtonForm="new-manual-batch-modal-form"
          handleCancel={disclosure.close}
        />
      </Box>
    </>
  );
}

function SettlementDetail({
  title,
  text,
  ...props
}: {
  title: string;
  text: string | undefined;
  'data-testid'?: string;
}) {
  return (
    <HStack w="100%" justifyContent="space-between">
      <Text>{title}</Text>
      <Text color="colorTextAttention" {...props}>
        {text}
      </Text>
    </HStack>
  );
}
